<template>
    <div>

        <vue-headful title="Account Setup Wizard :: MeritEHS" />

        <h4 class="py-3 mb-4">
            <ol class="breadcrumb font-weight-bold m-0">
                <li class="breadcrumb-item font-weight-light"><i class="fa fa-cogs"></i> Admin</li>
                <li class="breadcrumb-item active"> Account Setup Wizard</li>
                <li class="breadcrumb-item"><button class="btn btn-sm btn-secondary" @click="curTab = 'complete'">Skip Setup</button></li>
            </ol>
        </h4>

        <div v-if="loading">
            <i class="fa fa-circle-o-notch fa-spin"></i> Loading
        </div>
        <div v-else>
            <ul class="search-nav nav nav-tabs tabs-alt container-p-x container-m--x mb-4">
                <b-nav-item :active="curTab === 'branding'" @click="curTab = 'branding'">
                    Branding &amp; Logos
                </b-nav-item>
                <b-nav-item :active="curTab === 'sso'" @click="curTab = 'sso'">
                    Single-Sign On (SSO)
                </b-nav-item>
                <b-nav-item :active="curTab === 'jobsites'" @click="curTab = 'jobsites'">
                    Add Jobsites
                </b-nav-item>
                <b-nav-item :active="curTab === 'employees'" @click="curTab = 'employees'">
                    Add Employees <i class="fa fa-question-circle" title="Foreman, Leadman, Other"></i>
                </b-nav-item>
                <b-nav-item :active="curTab === 'users'" @click="curTab = 'users'">
                    Add Users <i class="fa fa-question-circle" title="Superintendents, Project Managers, Supervisors"></i>
                </b-nav-item>
                <b-nav-item :active="curTab === 'complete'" @click="curTab = 'complete'">
                    Setup Complete
                </b-nav-item>
            </ul>

            <div v-if="curTab === 'branding'">
                <div class="card mb-3">
                    <div class="card-header"><strong>Branding & Logos</strong></div>
                    <div class="card-body">
                        <div class="form-group row mb-4">
                            <label :class="{ 'col-lg-2 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.subdomain }">
                                Subdomain:
                            </label>
                            <div class="col-md-6 col-sm-8 col-12">
                                <b-form-input type="text" v-model="account.subdomain" :class="{'is-invalid': errors.subdomain}" placeholder="(Optional). Allows you to brand the urls with your company name."></b-form-input>

                                <span v-if="errors.subdomain" class="text-danger">
                                    <span v-for="error in errors.subdomain">{{ error }}</span>
                                </span>

                                <div class="alert alert-warning mt-2">
                                    <i class="fa fa-question-circle"></i> For example: https://<strong>yourcompanyname</strong>.meritehs.com. This will also allow you to add your logo to the login page.
                                </div>
                            </div>
                        </div>

                        <div class="form-group row" v-if="account.subdomain != '' && account.subdomain != null">
                            <label :class="{ 'col-lg-2 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.brand_logo_large }">
                                Login Page Logo:
                            </label>
                            <div class="col-md-6 col-sm-8 col-12">
                                <b-form-file accept="image/*" plain v-model="brand_logo_large" />

                                <div v-if="errors.brand_logo_large" class="text-danger">
                                    <span v-for="error in errors.brand_logo_large">{{ error }}</span>
                                </div>

                                <div class="alert alert-warning mt-2">
                                    <div>
                                        <i class="fa fa-question-circle"></i> Choose a logo to appear when a user goes to https://<strong>{{ account.subdomain }}</strong>.meritehs.com/login. If no logo is chosen, it will default to the MeritEHS logo.
                                    </div>

                                    <a href="/Images/meritehs-branding-example.png" class="d-block mt-2" style="width: 200px;">
                                        <img src="/Images/meritehs-branding-example.png" class="img-fluid" />
                                    </a>

                                    <a href="/Images/meritehs-branding-example.png" target="_blank" title="View the Branded Page Example. This link opens a new tab">View Example Image in a New Tab</a>
                                </div>
                            </div>
                        </div>
                        <hr />

                        <div class="form-group row" v-if="account.subdomain != '' && account.subdomain != null">
                            <label :class="{ 'col-lg-2 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.brand_logo_large }">
                                App Icon:
                            </label>
                            <div class="col-md-6 col-sm-8 col-12">
                                <b-form-file accept="image/*" plain @change="setImage"/>

                                <div class="mt-2">
                                    <label>Preview:</label>
                                    <img :src="cropImg" style="width: 30px; height: 30px; border: 1px solid gray" alt="CROP" />
                                    <a href="javascript:void(0)" v-if="cropImage" v-on:click="edit_app_icon = !edit_app_icon">
                                        <template v-if="edit_app_icon">Cancel</template>
                                        Edit App Icon
                                    </a>
                                </div>

                                <div v-show="edit_app_icon">
                                    <vue-cropper
                                    ref='cropper'
                                    :cropend="cropImage"
                                    :ready="cropImage"
                                    :guides="true"
                                    :view-mode="2"
                                    :auto-crop-area="1"
                                    drag-mode="crop"
                                    :min-container-width="200"
                                    :min-container-height="200"
                                    :background="true"
                                    :src="account.brand_logo"
                                    alt="Source Image"
                                    :aspectRatio="1"
                                    :img-style="{ 'width': '400px', 'height': '400px' }">
                                    </vue-cropper>

                                    <button class="btn btn-sm btn-success mt-1" v-on:click="cropAppIcon()">Crop App Icon</button>
                                </div>

                                <div v-if="errors.brand_logo_large" class="text-danger">
                                    <span v-for="error in errors.brand_logo_large">{{ error }}</span>
                                </div>

                                <div class="alert alert-warning mt-2">
                                    <div>
                                        <i class="fa fa-question-circle"></i> This will replace the default MeritEHS logo in the upper-left corner of the application for logged in users.
                                    </div>

                                    <a href="/Images/meritehs-app-icon-example.png" class="d-block mt-2" style="width: 200px;">
                                        <img src="/Images/meritehs-app-icon-example.png" class="img-fluid" />
                                    </a>

                                    <a href="/Images/meritehs-app-icon-example.png" target="_blank" title="View the Branded Page Example. This link opens a new tab">View Example Image in a New Tab</a>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="card-footer">
                        <button class="btn btn-sm btn-success" v-on:click="saveBranding()" :disabled="saving">
                            <template v-if="saving">Saving Branding <i class="fa fa-spinner fa-spin"></i></template>
                            <template v-else>Save Branding Setting</template>
                        </button>
                    </div>
                </div>

                <button class="btn btn-sm btn-info" v-on:click="curTab = 'sso'">Next Tab <i class="fa fa-caret-right"></i></button>
            </div>

            <div v-if="curTab === 'sso'">
                <div class="card mb-3">
                    <div class="card-header"><strong>Single-Sign-On (SSO)</strong></div>
                    <div class="card-body">
                        <div class="form-group row">
                            <label :class="{ 'col-lg-2 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.account_domain }">
                                Email Domain:
                            </label>
                            <div class="col-md-6 col-sm-8 col-12">
                                <b-form-input disabled type="text" :value="account.account_domain ? account.account_domain : '-- No Organization Domain Detected --'" :class="{'is-invalid': errors.account_domain}"></b-form-input>

                                <div v-if="errors.account_domain" class="text-danger">
                                    <span v-for="error in errors.account_domain">{{ error }}</span>
                                </div>

                                <div class="alert alert-warning mt-2">
                                    <div>
                                        <p><i class="fa fa-question-circle"></i> This is the email domain for your company. So in test@example.com, <strong>example.com</strong> is the domain. This value was auto-calculated when your MeritEHS account was created. If this domain does not match your Single-Sign On domain, contact us <router-link to="/bug">here</router-link> with your correct domain in the message to get it fixed.</p>

                                        <p>Turning on SSO allows anyone in your organization with a company email to auto-login to MeritEHS. However, they will not have any roles/permission, so they will be redirected to a "Request Roles" page where a role request will be emailed to all MeritEHS admins.</p>

                                        <p><strong>Note:</strong> 3rd Party Email Domains, such as @gmail.com, cannot be used for SSO. You must own the domain to use SSO.</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="form-group row">
                            <label class="col-lg-2 col-md-3 col-sm-4 col-form-label text-sm-right">
                                Login Type:
                            </label>
                            <div class="col-md-6 col-sm-8 col-12">
                                <b-form-select v-model="account.login_type">
                                    <option value="basic">Basic - User &amp; Password Login</option>
                                    <template v-if="account.account_domain">
                                        <option value="graph">Microsoft Office - SSO (Uses the Domain Above)</option>
                                    </template>
                                </b-form-select>

                                <div class="alert alert-warning mt-2">
                                    <div>
                                        <i class="fa fa-question-circle"></i> Login Type defaults to <strong>Basic</strong>, but changing this to Microsoft Office will activate SSO and use the domain above to auto-login new users.
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="form-group row" v-if="account.login_type === 'graph'">
                            <label class="col-lg-4 col-md-4 col-sm-6 col-form-label text-sm-right">
                                Enable SSO to Auto-Create Users: <i class="fa fa-question-circle" title="If checked, MeritEHS will auto-create users when they attempt to login via SSO. Permissions will be auto-assigned by matching the SSO Job Title to the assigned match values on any Role."></i>
                            </label>
                            <div class="col-md-6 col-sm-8 col-12">
                                <b-form-checkbox v-model="account.enable_sso_user_creation" :value="1" :unchecked-value="0" plain />
                            </div>
                        </div>
                    </div>
                    <div class="card-footer">
                        <button class="btn btn-sm btn-success" v-on:click="saveSSO()" :disabled="!account.account_domain">Save SSO Setting</button>
                    </div>
                </div>

                <button class="btn btn-sm btn-secondary" v-on:click="curTab = 'branding'"><i class="fa fa-caret-left"></i> Previous Tab</button>
                <button class="btn btn-sm btn-info" v-on:click="curTab = 'jobsites'">Next Tab <i class="fa fa-caret-right"></i></button>
            </div>

            <div v-if="curTab === 'jobsites'">
                <div class="card mb-3">
                    <div class="card-header"><strong>Company Divisions</strong></div>
                    <div class="card-body">
                        <table class="table table-bordered table-condensed table-small">
                            <thead>
                                <tr class="bg-light">
                                    <th>Division Code</th>
                                    <th>Division Name</th>
                                    <th style="min-width: 100px;">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="division in divisions" :class="new_division.DivisionId == division.DivisionId ? 'bg-light' : ''">
                                    <template v-if="new_division.DivisionId == division.DivisionId">
                                        <td>
                                            <b-form-input v-model="new_division.division_code" size="sm" placeholder="Enter Division Code" :class="{'is-invalid': errors.division_code}" />

                                            <span v-if="errors.division_code" class="text-danger">
                                                <span v-for="err in errors.division_code">{{ err }}</span>
                                            </span>
                                        </td>
                                        <td>
                                            <b-form-input v-model="new_division.DivisionName" size="sm" placeholder="Enter Division Name" :class="{'is-invalid': errors.DivisionName}" />

                                            <span v-if="errors.DivisionName" class="text-danger">
                                                <span v-for="err in errors.DivisionName">{{ err }}</span>
                                            </span>
                                        </td>
                                        <td>
                                            <button class="btn btn-sm btn-success" v-on:click="addDivision()" title="Save New Division" :disabled="saving_division">
                                                <i class="fa fa-plus"></i>
                                            </button>
                                            <button class="btn btn-sm btn-link" v-if="new_division.DivisionId" v-on:click="resetDivision()" title="Cancel Edit" :disabled="saving_division">
                                                <i class="fa fa-times"></i>
                                            </button>
                                        </td>
                                    </template>
                                    <template v-else>
                                        <td>{{ division.division_code }}</td>
                                        <td>
                                            {{ division.DivisionName }}
                                        </td>
                                        <td>
                                            <button class="btn btn-sm btn-link" v-on:click="editDivision(division)" title="Edit Division"><i class="fa fa-edit"></i></button>
                                            <button class="btn btn-sm btn-link" v-on:click="deleteDivision(division)" title="Delete Division" :disabled="deleting_division"><i class="fa fa-trash text-danger"></i></button>
                                        </td>
                                    </template>
                                </tr>
                            </tbody>
                            <tfoot v-if="!new_division.DivisionId">
                                <tr class="bg-light">
                                    <td>
                                        <b-form-input v-model="new_division.division_code" size="sm" placeholder="Enter Division Code" :class="{'is-invalid': errors.division_code}" />

                                        <span v-if="errors.division_code" class="text-danger">
                                            <span v-for="err in errors.division_code">{{ err }}</span>
                                        </span>
                                    </td>
                                    <td>
                                        <b-form-input v-model="new_division.DivisionName" size="sm" placeholder="Enter Division Name" :class="{'is-invalid': errors.DivisionName}" />

                                        <span v-if="errors.DivisionName" class="text-danger">
                                            <span v-for="err in errors.DivisionName">{{ err }}</span>
                                        </span>
                                    </td>
                                    <td>
                                        <button class="btn btn-sm btn-success" v-on:click="addDivision()" title="Save New Division" :disabled="saving_division">
                                            <i class="fa fa-plus"></i>
                                        </button>
                                        <button class="btn btn-sm btn-link" v-if="new_division.DivisionId" v-on:click="resetDivision()" title="Cancel Edit" :disabled="saving_division">
                                            <i class="fa fa-times"></i>
                                        </button>
                                    </td>
                                </tr>
                            </tfoot>
                        </table>

                        <div v-if="errors.other_division_id" class="alert alert-danger">
                            <button type="button" class="close" v-on:click="errors.other_division_id = null"><span aria-hidden="true">&times;</span></button>
                            The division you are trying to delete is in use by other data records. Visit the <router-link :to="`/admin/divisions`">Divisions List</router-link> to perform a Merge &amp; Delete.
                        </div>
                    </div>
                </div>

                <div class="card mb-3">
                    <div class="card-header"><strong>Jobsites</strong></div>
                    <div class="card-body">
                        <div class="alert alert-info mb-2">
                            <i class="fa fa-file-excel-o"></i> For a large list of Jobsites, you can import a spreadsheet of Jobsites. <a href="/jobsites/import" target="_blank">See Jobsite Import</a>
                        </div>
                        <table class="table table-bordered table-condensed table-small">
                            <thead>
                                <tr class="bg-light">
                                    <th>Jobsite#</th>
                                    <th>Jobsite Name</th>
                                    <th>Division</th>
                                    <th style="min-width: 100px;">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="jobsite in jobsites" :class="new_jobsite.JobsiteId == jobsite.JobsiteId ? 'bg-light' : ''">
                                    <template v-if="new_jobsite.JobsiteId == jobsite.JobsiteId">
                                        <td>
                                            <b-form-input v-model="new_jobsite.JobsiteNumber" size="sm" placeholder="Enter Jobsite Number" :class="{'is-invalid': errors.JobsiteNumber}" />

                                            <span v-if="errors.JobsiteNumber" class="text-danger">
                                                <span v-for="err in errors.JobsiteNumber">{{ err }}</span>
                                            </span>
                                        </td>
                                        <td>
                                            <b-form-input v-model="new_jobsite.JobsiteName" size="sm" placeholder="Enter Jobsite Number" :class="{'is-invalid': errors.JobsiteName}" />

                                            <span v-if="errors.JobsiteName" class="text-danger">
                                                <span v-for="err in errors.JobsiteName">{{ err }}</span>
                                            </span>
                                        </td>
                                        <td>
                                            <b-form-select v-model="new_jobsite.DivisionId" size="sm" :options="divisionOptions" :class="{'is-invalid': errors.DivisionId}" />

                                            <span v-if="errors.DivisionId" class="text-danger">
                                                <span v-for="err in errors.DivisionId">{{ err }}</span>
                                            </span>
                                        </td>
                                        <td>
                                            <button class="btn btn-sm btn-success" v-on:click="addJobsite()" title="Save New Jobsite" :disabled="saving_jobsite">
                                                <i class="fa fa-plus"></i>
                                            </button>
                                            <button class="btn btn-sm btn-link" v-if="new_jobsite.JobsiteId" v-on:click="resetJobsite()" title="Cancel Edit" :disabled="saving_jobsite">
                                                <i class="fa fa-times"></i>
                                            </button>
                                        </td>
                                    </template>
                                    <template v-else>
                                        <td>{{ jobsite.JobsiteNumber }}</td>
                                        <td>{{ jobsite.JobsiteName }}</td>
                                        <td>{{ jobsite.division_code }} - {{ jobsite.DivisionName }}</td>
                                        <td>
                                            <button class="btn btn-sm btn-link" v-on:click="editJobsite(jobsite)"><i class="fa fa-edit"></i></button>
                                            <button class="btn btn-sm btn-link" v-on:click="deleteJobsite(jobsite)" title="Delete Jobsite" :disabled="deleting_jobsite"><i class="fa fa-trash text-danger"></i></button>
                                        </td>
                                    </template>
                                </tr>
                            </tbody>
                            <tfoot v-if="!new_jobsite.JobsiteId">
                                <tr class="bg-light">
                                    <td>
                                        <b-form-input v-model="new_jobsite.JobsiteNumber" size="sm" placeholder="Enter Jobsite Number" :class="{'is-invalid': errors.JobsiteNumber}" />

                                        <span v-if="errors.JobsiteNumber" class="text-danger">
                                            <span v-for="err in errors.JobsiteNumber">{{ err }}</span>
                                        </span>
                                    </td>
                                    <td>
                                        <b-form-input v-model="new_jobsite.JobsiteName" size="sm" placeholder="Enter Jobsite Number" :class="{'is-invalid': errors.JobsiteName}" />

                                        <span v-if="errors.JobsiteName" class="text-danger">
                                            <span v-for="err in errors.JobsiteName">{{ err }}</span>
                                        </span>
                                    </td>
                                    <td>
                                        <b-form-select v-model="new_jobsite.DivisionId" size="sm" :options="divisionOptions" :class="{'is-invalid': errors.DivisionId}" />

                                        <span v-if="errors.DivisionId" class="text-danger">
                                            <span v-for="err in errors.DivisionId">{{ err }}</span>
                                        </span>
                                    </td>
                                    <td>
                                        <button class="btn btn-sm btn-success" v-on:click="addJobsite()" title="Save New Jobsite" :disabled="saving_jobsite">
                                            <i class="fa fa-plus"></i>
                                        </button>
                                        <button class="btn btn-sm btn-link" v-if="new_jobsite.JobsiteId" v-on:click="resetJobsite()" title="Cancel Edit" :disabled="saving_jobsite">
                                            <i class="fa fa-times"></i>
                                        </button>
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>

                <button class="btn btn-sm btn-secondary" v-on:click="curTab = 'sso'"><i class="fa fa-caret-left"></i> Previous Tab</button>
                <button class="btn btn-sm btn-info" v-on:click="curTab = 'employees'">Next Tab <i class="fa fa-caret-right"></i></button>
            </div>

            <div v-if="curTab === 'employees'">
                <div class="card mb-3">
                    <div class="card-header"><strong>Add Employees</strong></div>
                    <div class="card-body">
                        <div class="alert alert-warning mb-2">
                            <i class="fa fa-question-circle"></i> Employee records are used to associate Foreman, Leadman, and others on Incidents.
                        </div>
                        <div class="alert alert-info mb-2">
                            <i class="fa fa-file-excel-o"></i> For a large list of employees, you can import a spreadsheet of Employees. <a href="/employees/import" target="_blank">See Employee Import</a>
                        </div>
                        <table class="table table-bordered table-condensed table-small">
                            <thead>
                                <tr class="bg-light">
                                    <th>Employee#</th>
                                    <th>First Name</th>
                                    <th>Last Name</th>
                                    <th>Division</th>
                                    <th style="min-width: 100px;">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="employee in employees" :class="new_employee.EmployeeId == employee.EmployeeId ? 'bg-light' : ''">
                                    <template v-if="new_employee.EmployeeId == employee.EmployeeId">
                                        <td>
                                            <b-form-input v-model="new_employee.EmployeeNumber" size="sm" placeholder="Enter Employee Number" :class="{'is-invalid': errors.EmployeeNumber}" />

                                            <span v-if="errors.EmployeeNumber" class="text-danger">
                                                <span v-for="err in errors.EmployeeNumber">{{ err }}</span>
                                            </span>
                                        </td>
                                        <td>
                                            <b-form-input v-model="new_employee.FirstName" size="sm" placeholder="Enter First Name" :class="{'is-invalid': errors.FirstName}" />

                                            <span v-if="errors.FirstName" class="text-danger">
                                                <span v-for="err in errors.FirstName">{{ err }}</span>
                                            </span>
                                        </td>
                                        <td>
                                            <b-form-input v-model="new_employee.LastName" size="sm" placeholder="Enter Last Name" :class="{'is-invalid': errors.LastName}" />

                                            <span v-if="errors.LastName" class="text-danger">
                                                <span v-for="err in errors.LastName">{{ err }}</span>
                                            </span>
                                        </td>
                                        <td>
                                            <b-form-select v-model="new_employee.division_id" size="sm" :options="divisionOptions" :class="{'is-invalid': errors.division_id}" />

                                            <span v-if="errors.division_id" class="text-danger">
                                                <span v-for="err in errors.division_id">{{ err }}</span>
                                            </span>
                                        </td>
                                        <td>
                                            <button class="btn btn-sm btn-success" v-on:click="addEmployee()" title="Save New Jobsite" :disabled="saving_employee">
                                                <i class="fa fa-plus"></i>
                                            </button>
                                            <button class="btn btn-sm btn-link" v-if="new_employee.EmployeeId" v-on:click="resetEmployee()" title="Cancel Edit" :disabled="saving_employee">
                                                <i class="fa fa-times"></i>
                                            </button>
                                        </td>
                                    </template>
                                    <template v-else>
                                        <td>{{ employee.EmployeeNumber }}</td>
                                        <td>{{ employee.FirstName }}</td>
                                        <td>{{ employee.LastName }}</td>
                                        <td>{{ employee.division_code }} - {{ employee.DivisionName }}</td>
                                        <td>
                                            <button class="btn btn-sm btn-link" v-on:click="editEmployee(employee)"><i class="fa fa-edit"></i></button>
                                            <button class="btn btn-sm btn-link" v-on:click="deleteEmployee(employee)"><i class="fa fa-trash text-danger"></i></button>
                                        </td>
                                    </template>
                                </tr>
                            </tbody>
                            <tfoot v-if="!new_employee.EmployeeId">
                                <tr class="bg-light">
                                    <td>
                                        <b-form-input v-model="new_employee.EmployeeNumber" size="sm" placeholder="Enter Employee Number" :class="{'is-invalid': errors.EmployeeNumber}" />

                                        <span v-if="errors.EmployeeNumber" class="text-danger">
                                            <span v-for="err in errors.EmployeeNumber">{{ err }}</span>
                                        </span>
                                    </td>
                                    <td>
                                        <b-form-input v-model="new_employee.FirstName" size="sm" placeholder="Enter First Name" :class="{'is-invalid': errors.FirstName}" />

                                        <span v-if="errors.FirstName" class="text-danger">
                                            <span v-for="err in errors.FirstName">{{ err }}</span>
                                        </span>
                                    </td>
                                    <td>
                                        <b-form-input v-model="new_employee.LastName" size="sm" placeholder="Enter Last Name" :class="{'is-invalid': errors.LastName}" />

                                        <span v-if="errors.LastName" class="text-danger">
                                            <span v-for="err in errors.LastName">{{ err }}</span>
                                        </span>
                                    </td>
                                    <td>
                                        <b-form-select v-model="new_employee.division_id" size="sm" :options="divisionOptions" :class="{'is-invalid': errors.division_id}" />

                                        <span v-if="errors.division_id" class="text-danger">
                                            <span v-for="err in errors.division_id">{{ err }}</span>
                                        </span>
                                    </td>
                                    <td>
                                        <button class="btn btn-sm btn-success" v-on:click="addEmployee()" title="Save New Jobsite" :disabled="saving_employee">
                                            <i class="fa fa-plus"></i>
                                        </button>
                                        <button class="btn btn-sm btn-link" v-if="new_employee.EmployeeId" v-on:click="resetEmployee()" title="Cancel Edit" :disabled="saving_employee">
                                            <i class="fa fa-times"></i>
                                        </button>
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>

                <button class="btn btn-sm btn-secondary" v-on:click="curTab = 'jobsites'"><i class="fa fa-caret-left"></i> Previous Tab</button>
                <button class="btn btn-sm btn-info" v-on:click="curTab = 'users'">Next Tab <i class="fa fa-caret-right"></i></button>
            </div>

            <div v-if="curTab === 'users'">
                <div class="card mb-3">
                    <div class="card-header"><strong>Add Users</strong></div>
                    <div class="card-body">
                        <div class="alert alert-warning mb-2">
                            <i class="fa fa-question-circle"></i> User records are used to associate Superintendents, Project Managers, and Supervisors on Incidents, Observations, Inspections, and more.<br/>
                            <strong>Billing Note:</strong> The users added here will <strong>not</strong> count towards your billing total, unless they login and use MeritEHS.
                        </div>
                        <table class="table table-bordered table-condensed table-small">
                            <thead>
                                <tr class="bg-light">
                                    <th>First Name</th>
                                    <th>Last Name</th>
                                    <th>Email</th>
                                    <th>Title</th>
                                    <th style="min-width: 100px;">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="user in users" :class="new_user.UserId == user.UserId ? 'bg-light' : ''">
                                    <template v-if="new_user.UserId == user.UserId">
                                        <td>
                                            <b-form-input v-model="new_user.FirstName" size="sm" placeholder="Enter First Name" :class="{'is-invalid': errors.FirstName}" />

                                            <span v-if="errors.FirstName" class="text-danger">
                                                <span v-for="err in errors.FirstName">{{ err }}</span>
                                            </span>
                                        </td>
                                        <td>
                                            <b-form-input v-model="new_user.LastName" size="sm" placeholder="Enter Last Name" :class="{'is-invalid': errors.LastName}" />

                                            <span v-if="errors.LastName" class="text-danger">
                                                <span v-for="err in errors.LastName">{{ err }}</span>
                                            </span>
                                        </td>
                                        <td>
                                            <b-form-input v-model="new_user.email" size="sm" placeholder="Enter Email" :class="{'is-invalid': errors.email}" />

                                            <span v-if="errors.email" class="text-danger">
                                                <span v-for="err in errors.email">{{ err }}</span>
                                            </span>

                                            <span v-if="errors.existing_user" class="text-danger">
                                                User already exists on your account. <a :href="`/users/view/${errors.existing_user[0]}`" target="_blank">See User Record</a>
                                            </span>
                                        </td>
                                        <td>
                                            <b-form-input v-model="new_user.title" size="sm" placeholder="Enter Title" :class="{'is-invalid': errors.title}" />

                                            <span v-if="errors.title" class="text-danger">
                                                <span v-for="err in errors.title">{{ err }}</span>
                                            </span>
                                        </td>
                                        <td>
                                            <button class="btn btn-sm btn-success" v-on:click="addUser()" title="Save New Jobsite" :disabled="saving_user">
                                                <i class="fa fa-plus"></i>
                                            </button>
                                            <button class="btn btn-sm btn-link" v-if="new_user.UserId" v-on:click="resetUser()" title="Cancel Edit" :disabled="saving_user">
                                                <i class="fa fa-times"></i>
                                            </button>
                                        </td>
                                    </template>
                                    <template v-else>
                                        <td>{{ user.FirstName }}</td>
                                        <td>{{ user.LastName }}</td>
                                        <td>{{ user.email }}</td>
                                        <td>{{ user.title }}</td>
                                        <td>
                                            <button class="btn btn-sm btn-link" v-on:click="editUser(user)"><i class="fa fa-edit"></i></button>
                                            <button class="btn btn-sm btn-link" v-on:click="deleteUser(user)" :disabled="deleting_user"><i class="fa fa-trash text-danger"></i></button>
                                        </td>
                                    </template>
                                </tr>
                            </tbody>
                            <tfoot v-if="!new_user.UserId">
                                <tr class="bg-light">
                                    <td>
                                        <b-form-input v-model="new_user.FirstName" size="sm" placeholder="Enter First Name" :class="{'is-invalid': errors.FirstName}" />

                                        <span v-if="errors.FirstName" class="text-danger">
                                            <span v-for="err in errors.FirstName">{{ err }}</span>
                                        </span>
                                    </td>
                                    <td>
                                        <b-form-input v-model="new_user.LastName" size="sm" placeholder="Enter Last Name" :class="{'is-invalid': errors.LastName}" />

                                        <span v-if="errors.LastName" class="text-danger">
                                            <span v-for="err in errors.LastName">{{ err }}</span>
                                        </span>
                                    </td>
                                    <td>
                                        <b-form-input v-model="new_user.email" size="sm" placeholder="Enter Email" :class="{'is-invalid': errors.email}" />

                                        <span v-if="errors.email" class="text-danger">
                                            <span v-for="err in errors.email">{{ err }}</span>
                                        </span>

                                        <span v-if="errors.existing_user" class="text-danger">
                                            User already exists on your account. <a :href="`/users/view/${errors.existing_user[0]}`" target="_blank">See User Record</a>
                                        </span>
                                    </td>
                                    <td>
                                        <b-form-input v-model="new_user.title" size="sm" placeholder="Enter Title" :class="{'is-invalid': errors.title}" />

                                        <span v-if="errors.title" class="text-danger">
                                            <span v-for="err in errors.title">{{ err }}</span>
                                        </span>
                                    </td>
                                    <td>
                                        <button class="btn btn-sm btn-success" v-on:click="addUser()" title="Save New Jobsite" :disabled="saving_user">
                                            <i class="fa fa-plus"></i>
                                        </button>
                                        <button class="btn btn-sm btn-link" v-if="new_user.UserId" v-on:click="resetUser()" title="Cancel Edit" :disabled="saving_user">
                                            <i class="fa fa-times"></i>
                                        </button>
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>

                <button class="btn btn-sm btn-secondary" v-on:click="curTab = 'employees'"><i class="fa fa-caret-left"></i> Previous Tab</button>
                <button class="btn btn-sm btn-info" v-on:click="curTab = 'complete'">Next Tab <i class="fa fa-caret-right"></i></button>
            </div>

            <div v-if="curTab === 'complete'">
                <div class="card mb-3">
                    <div class="card-header"><strong>All Done!</strong></div>
                    <div class="card-body">
                        <div class="alert alert-success mb-4">
                            <p>You can always change these settings, add more Jobsites, Employees, and Users, and manager other account settings from the Admin Settings.</p>
                            <div class="row">
                                <div class="col-md-4 col-sm-6 col-12">
                                    <a href="/Images/setup-admin.png" target="_blank" class="mt-2">
                                        <img src="/Images/setup-admin.png" class="img-fluid" />
                                    </a>
                                </div>
                            </div>
                        </div>

                        <h5>Need Additional Support?</h5>
                        <div class="alert alert-warning mb-4">
                            For all support related items, questions about setup, or issues you may be having, visit the <router-link to="/bug">Submit a Bug/Question</router-link> to submit a ticket to our support team.

                            <p>You can visit this page anytime by click the link in the dropdown next to your name:</p>

                            <div class="row">
                                <div class="col-md-4 col-sm-6 col-12">
                                    <a href="/Images/setup-bug.png" target="_blank" class="mt-2">
                                        <img src="/Images/setup-bug.png" class="img-fluid" />
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <button class="btn btn-sm btn-secondary" v-on:click="curTab = 'users'"><i class="fa fa-caret-left"></i> Previous Tab</button>
            </div>
        </div>
    </div>
</template>

<script>

    export default {
        data: function() {
            return {
                loading: true,
                saving: false,
                saving_division: false,
                saving_jobsite: false,
                saving_employee: false,
                saving_user: false,
                deleting_division: false,
                deleting_jobsite: false,
                deleting_employee: false,
                deleting_user: false,
                edit_app_icon: false,
                app_icon_blob: null,
                curTab: 'branding',
                cropImg: '',
                errors: {},
                account: {},
                brand_logo_large: null,
                new_jobsite: {
                    DivisionId: null,
                },
                new_division: {},
                new_employee: {
                    division_id: null,
                },
                new_user: {
                    IsActive: 1,
                    timezone_slug: this.$store.state.user.timezone,
                },
                divisions: [],
                jobsites: [],
                employees: [],
                users: [],
            }
        },

        computed: {
            divisionOptions(){
                //Setup
                var options = [
                    {value: null, text: '-- Select a Division --'}
                ];

                //Loop and Build
                for(var i = 0; i < this.divisions.length; i++)
                {
                    options.push({value: this.divisions[i].DivisionId, text: (this.divisions[i].division_code + ' - ' + this.divisions[i].DivisionName)});
                }

                //Send it
                return options;
            },
        },

        methods: {

            getSetupWizard: function() {
                //Get the data
                this.$http
                    .get('admin/setup-wizard')
                    .then(response => {
                        //Set all the data
                        this.account = response.data.account;
                        this.divisions = response.data.divisions;
                        this.jobsites = response.data.jobsites;
                        this.employees = response.data.employees;

                        //Check for App Icon
                        if(this.account.brand_logo)
                        {
                            //Set the Cropped Image
                            this.cropImg = this.account.brand_logo;
                        }

                    })
                    .catch(response => {
                        //Show an error
                    })
                    .then(() => {
                        //Set loading
                        this.loading = false;
                    });
            },

            saveBranding(){
                //Reset the errors
                this.errors = [];
                this.saving = true;

                //Check if we need to crop the image
                if(this.edit_app_icon)
                {
                    //Crop the Image Automatically
                    this.cropAppIcon(true);

                    //Stop here
                    return;
                }

                //Build the images
                var images = [
                    this.app_icon_blob,
                    this.brand_logo_large,
                ];

                //Send it
                this.$http
                    .postWithFiles('admin/settings', this.account, images)
                    .then(response => {
                        //Alert the user?
                        this.$toastr.success('Branding & Logos Saved');

                        //Send to the next tab
                        this.curTab = 'sso';

                        //Commit the User
                        let user = this.$store.state.user;
                        user.brand_logo = response.data + '?' + new Date().getTime();
                        user = this.putSettingsInStore(user);
                        this.$store.commit('login', JSON.parse(JSON.stringify(user)));

                        // Reset what the "original" is
                        //this.original_user = JSON.parse(JSON.stringify(this.$store.state.user));
                    })
                    .catch(error => {
                        //Check for error
                        if (error.response.status === 422) {
                            //Display validation errors
                            this.errors = error.response.data
                        }
                    })
                    .then(response => {
                        //SHow loading
                        this.saving = false;
                        //delete this.account.includes_files;
                    });
            },

            saveSSO(){
                //Send it
                this.$http
                    .post('admin/settings', this.account)
                    .then(response => {
                        //Alert the user?
                        this.$toastr.success('SSO Settings Saved');

                        //Send to the next tab
                        this.curTab = 'jobsites';

                        //Commit the User
                        let user = this.$store.state.user;
                        user.brand_logo = response.data + '?' + new Date().getTime();
                        user = this.putSettingsInStore(user);
                        this.$store.commit('login', JSON.parse(JSON.stringify(user)));

                        // Reset what the "original" is
                        //this.original_user = JSON.parse(JSON.stringify(this.$store.state.user));
                    })
                    .catch(error => {
                        //Check for error
                        if (error.response.status === 422) {
                            //Display validation errors
                            this.errors = error.response.data
                        }
                    })
                    .then(response => {
                        //SHow loading
                        this.saving = false;
                        //delete this.account.includes_files;
                    });
            },

            cropAppIcon(saveBranding = false){
                //Call it
                this.$refs.cropper.getCroppedCanvas().toBlob(blob => {
                    //Set the helper
                    this.app_icon_blob = blob;

                    //Crop the Image
                    this.cropImage();

                    //Turn it off
                    this.edit_app_icon = false;

                    //Check for Callback
                    if(saveBranding)
                    {
                        //Send it
                        this.saveBranding();
                    }
                });
            },

            setImage(e) {
                //Set the file
                const file = e.target.files[0];

                //Check the type
                if (file.type.indexOf('image/') === -1)
                {
                    alert('Please select an image file');
                    return;
                }

                //Check for a function
                if (typeof FileReader === 'function')
                {
                    const reader = new FileReader();
                    reader.onload = (event) => {
                        this.imgSrc = event.target.result;
                        // rebuild cropperjs with the updated source
                        this.$refs.cropper.replace(event.target.result);

                        //Show the Cropper
                        this.edit_app_icon = true;
                    };
                    reader.readAsDataURL(file);
                } else {
                    alert('Sorry, FileReader API not supported');
                }
            },

            cropImage() {
                // get image data for post processing, e.g. upload or setting image src
                this.cropImg = this.$refs.cropper.getCroppedCanvas().toDataURL();
            },

            putSettingsInStore(user) {
                user.brand_color = this.account.brand_color;
                user.brand_text_color = this.account.brand_text_color;
                user.standard_ppe = this.account.standard_ppe;

                return user;
            },

            addDivision(){
                //Helpers
                this.saving_division = true;

                //Send it
                this.$http
                    .post('admin/divisions-edit'  + (this.new_division.DivisionId ? '/' + this.new_division.DivisionId : ''), this.new_division)
                    .then(response => {
                        this.errors = {};

                        //Alert the user?
                        this.$toastr.success('Division Saved');

                        //Reset
                        this.resetDivision();

                        //Push Or Update
                        for (var i = this.divisions.length - 1; i >= 0; i--)
                        {
                            //Check All IDs
                            if(this.divisions[i].DivisionId == response.data.DivisionId)
                            {
                                //Splice it in
                                this.divisions.splice(i, 1, response.data);

                                //Stop
                                return;
                            }
                        }

                        //Default is push new
                        this.divisions.push(response.data);
                    })
                    .catch(error => {
                        //Check for error
                        if (error.response.status === 422) {
                            //Display validation errors
                            this.errors = error.response.data
                        }
                    })
                    .then(response => {
                        //SHow loading
                        this.saving_division = false;
                        //delete this.account.includes_files;
                    });
            },

            editDivision(division){
                //Set it
                this.new_division = JSON.parse(JSON.stringify(division));
            },

            deleteDivision(division){
                //Send it and show loading
                this.deleting_division = true;

                //Send it
                this.$http
                    .post('admin/divisions-delete/' + division.DivisionId, {DivisionId: division.DivisionId})
                    .then(response => {
                        this.errors = {};

                        //Alert the user?
                        this.$toastr.success('Division Deleted');

                        //Push Or Update
                        for (var i = this.divisions.length - 1; i >= 0; i--)
                        {
                            //Check All IDs
                            if(this.divisions[i].DivisionId == division.DivisionId)
                            {
                                //Splice it in
                                this.divisions.splice(i, 1);

                                //Stop
                                return;
                            }
                        }
                    })
                    .catch(error => {
                        //Check for error
                        if (error.response.status === 422)
                        {
                            //Display validation errors
                            this.errors = error.response.data
                        }
                    })
                    .then(response => {
                        //SHow loading
                        this.deleting_division = false;
                        //delete this.account.includes_files;
                    });
            },

            resetDivision(){
                //Reset
                this.new_division = {};
            },

            addJobsite(){
                //Helpers
                this.saving_jobsite = true;

                //Send it
                this.$http
                    .post('jobsites/edit'  + (this.new_jobsite.JobsiteId ? '/' + this.new_jobsite.JobsiteId : ''), this.new_jobsite)
                    .then(response => {
                        this.errors = {};

                        //Alert the user?
                        this.$toastr.success('Jobsite Saved');

                        //Reset
                        this.resetJobsite();

                        //Push Or Update
                        for (var i = this.jobsites.length - 1; i >= 0; i--)
                        {
                            //Check All IDs
                            if(this.jobsites[i].JobsiteId == response.data.JobsiteId)
                            {
                                //Splice it in
                                this.jobsites.splice(i, 1, response.data);

                                //Stop
                                return;
                            }
                        }

                        //Default is push new
                        this.jobsites.push(response.data);
                    })
                    .catch(error => {
                        //Check for error
                        if (error.response.status === 422) {
                            //Display validation errors
                            this.errors = error.response.data
                        }
                    })
                    .then(response => {
                        //SHow loading
                        this.saving_jobsite = false;
                        //delete this.account.includes_files;
                    });
            },

            editJobsite(jobsite){
                //Set it
                this.new_jobsite = JSON.parse(JSON.stringify(jobsite));
                this.new_jobsite.DivisionName = undefined;
                this.new_jobsite.division_code = undefined;
            },

            deleteJobsite(jobsite){
                //Send it and show loading
                this.deleting_jobsite = true;

                //Send it
                this.$http
                    .post('/jobsites/merge', {JobsiteId: jobsite.JobsiteId})
                    .then(response => {
                        //Alert the user?
                        this.$toastr.success('Jobsite Deleted');

                        //Push Or Update
                        for (var i = this.jobsites.length - 1; i >= 0; i--)
                        {
                            //Check All IDs
                            if(this.jobsites[i].JobsiteId == jobsite.JobsiteId)
                            {
                                //Splice it in
                                this.jobsites.splice(i, 1);

                                //Stop
                                return;
                            }
                        }
                    })
                    .catch(error => {
                        //Check for error
                        if (error.response.status === 422)
                        {
                            //Display validation errors
                            this.errors = error.response.data;

                            //Check for the reassign
                            if(this.errors.reassign_JobsiteId)
                            {
                                this.$toastr.error('The jobsite you are trying to delete is in use by other data records. Visit the Jobsite Page to perform a Merge & Delete.');
                            }
                        }
                    })
                    .then(response => {
                        //SHow loading
                        this.deleting_jobsite = false;

                        //Reset the errors
                        this.errors = {};
                    });
            },

            resetJobsite(){
                //Reset
                this.new_jobsite = {
                    DivisionId: null,
                };
            },

            addEmployee(){
                //Helpers
                this.saving_employee = true;

                //Send it
                this.$http
                    .post('employees/edit'  + (this.new_employee.EmployeeId ? '/' + this.new_employee.EmployeeId : ''), this.new_employee)
                    .then(response => {
                        this.errors = {};

                        //Alert the user?
                        this.$toastr.success('Employee Saved');

                        //Reset
                        this.resetEmployee();

                        //Push Or Update
                        for (var i = this.employees.length - 1; i >= 0; i--)
                        {
                            //Check All IDs
                            if(this.employees[i].EmployeeId == response.data.EmployeeId)
                            {
                                //Splice it in
                                this.employees.splice(i, 1, response.data);

                                //Stop
                                return;
                            }
                        }

                        //Default is push new
                        this.employees.push(response.data);
                    })
                    .catch(error => {
                        //Check for error
                        if (error.response.status === 422) {
                            //Display validation errors
                            this.errors = error.response.data
                        }
                    })
                    .then(response => {
                        //SHow loading
                        this.saving_employee = false;
                        //delete this.account.includes_files;
                    });
            },

            editEmployee(employee){
                //Set it
                this.new_employee = JSON.parse(JSON.stringify(employee));
            },

            deleteEmployee(employee){
                //Send it and show loading
                this.deleting_employee = true;

                //Send it
                this.$http
                    .post('/employees/merge', {EmployeeId: employee.EmployeeId})
                    .then(response => {
                        //Alert the user?
                        this.$toastr.success('Employee Deleted');

                        //Push Or Update
                        for (var i = this.employees.length - 1; i >= 0; i--)
                        {
                            //Check All IDs
                            if(this.employees[i].EmployeeId == employee.EmployeeId)
                            {
                                //Splice it in
                                this.employees.splice(i, 1);

                                //Stop
                                return;
                            }
                        }
                    })
                    .catch(error => {
                        //Check for error
                        if (error.response.status === 422)
                        {
                            //Display validation errors
                            this.errors = error.response.data;
                        }
                    })
                    .then(response => {
                        //SHow loading
                        this.deleting_employee = false;

                        //Reset the errors
                        this.errors = {};
                    });
            },

            resetEmployee(){
                //Reset
                this.new_employee = {
                    division_id: null,
                };
            },

            addUser(){
                //Helpers
                this.saving_user = true;

                //Send it
                this.$http
                    .post('users/edit'  + (this.new_user.UserId ? '/' + this.new_user.UserId : ''), this.new_user)
                    .then(response => {
                        this.errors = {};

                        //Alert the user?
                        this.$toastr.success('User Saved');

                        //Reset
                        this.resetUser();

                        //Push Or Update
                        for (var i = this.users.length - 1; i >= 0; i--)
                        {
                            //Check All IDs
                            if(this.users[i].UserId == response.data.UserId)
                            {
                                //Splice it in
                                this.users.splice(i, 1, response.data);

                                //Stop
                                return;
                            }
                        }

                        //Default is push new
                        this.users.push(response.data);
                    })
                    .catch(error => {
                        //Check for error
                        if (error.response.status === 422) {
                            //Display validation errors
                            this.errors = error.response.data
                        }
                    })
                    .then(response => {
                        //SHow loading
                        this.saving_user = false;
                        //delete this.account.includes_files;
                    });
            },

            editUser(user){
                //Set it
                this.new_user = JSON.parse(JSON.stringify(user));
            },

            deleteUser(user){
                //Send it and show loading
                this.deleting_user = true;

                //Send it
                this.$http
                    .post('/users/delete/' + user.UserId)
                    .then(response => {
                        //Alert the user?
                        this.$toastr.success('User Deleted');

                        //Push Or Update
                        for (var i = this.users.length - 1; i >= 0; i--)
                        {
                            //Check All IDs
                            if(this.users[i].UserId == user.UserId)
                            {
                                //Splice it in
                                this.users.splice(i, 1);

                                //Stop
                                return;
                            }
                        }
                    })
                    .catch(error => {
                        //Check for error
                        if (error.response.status === 422)
                        {
                            //Display validation errors
                            this.errors = error.response.data;
                        }
                    })
                    .then(response => {
                        //SHow loading
                        this.deleting_user = false;

                        //Reset the errors
                        this.errors = {};
                    });
            },

            resetUser(){
                //Reset
                this.new_user = {
                    IsActive: 1,
                    timezone_slug: this.$store.state.user.timezone,
                };
            },
        },

        mounted() {
            //Get the incidents
            this.getSetupWizard();
        }
    }
</script>
